import { FC, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Checkbox,
  FormControl,
  FormHelperText,
  Input,
  Text,
  Textarea,
  VStack,
  Icon,
} from '@chakra-ui/react';
import { DonationFormLabel } from '../donationFormLabel';
import { DonationField } from '../../constants';
import { Info } from '../../assets';
import { getDataTracking } from '../../utils';

export interface DonorRecognitionProps {
  hasUserModifiedDisplayName: boolean;
  displayNameModified: () => void;
}

export const DonorRecognition: FC<DonorRecognitionProps> = ({
  hasUserModifiedDisplayName,
  displayNameModified,
}): JSX.Element => {
  const { register, watch, getValues, setValue } = useFormContext();
  const [isAnonymous, setIsAnonymous] = useState<boolean>(false);
  const inputFieldsWatch = watch([
    DonationField.firstName,
    DonationField.lastName,
  ]);

  useEffect(() => {
    const defaultDonorName = getValues([
      DonationField.firstName,
      DonationField.lastName,
    ]).join(' ');
    if (!hasUserModifiedDisplayName) {
      setValue(DonationField.donationDisplayName, defaultDonorName);
    }
  }, [inputFieldsWatch, hasUserModifiedDisplayName, getValues, setValue]);

  return (
    <VStack align="left" spacing={6} w="full">
      <Text as="h3" fontSize="1.5rem">
        Donation Recognition
      </Text>

      <FormControl>
        <DonationFormLabel htmlFor={DonationField.donationDisplayName}>
          Donation Display Name
        </DonationFormLabel>
        <FormHelperText>
          This will be shown in donor list and viewable by others
        </FormHelperText>
        <Input
          {...getDataTracking('p2p_df_setdisplayname')}
          id={DonationField.donationDisplayName}
          {...register(DonationField.donationDisplayName)}
          disabled={isAnonymous}
          maxLength={50}
          onChange={displayNameModified}
        />
      </FormControl>

      <FormControl align="left" as={VStack} spacing={4}>
        <Checkbox
          defaultChecked={false}
          id={DonationField.hideMyDisplayName}
          {...getDataTracking('p2p_df_hidemyname')}
          {...register(DonationField.hideMyDisplayName)}
          onChange={(e) => {
            setIsAnonymous(e.target.checked);
            setValue(DonationField.donationDisplayName, 'Anonymous');
          }}
        >
          Hide my name
        </Checkbox>

        <Checkbox
          id={DonationField.hideDonationAmount}
          {...getDataTracking('p2p_df_hideamount')}
          {...register(DonationField.hideDonationAmount)}
        >
          Hide my donation amount
        </Checkbox>

        <FormHelperText>
          <Icon as={Info} h={3} w={3} /> Fundraisers can still see your name and
          donation amount on fundraising reports.
        </FormHelperText>
      </FormControl>

      <FormControl>
        <DonationFormLabel htmlFor={DonationField.donationPersonalNote}>
          Send a Personal Note
        </DonationFormLabel>
        <Textarea
          id={DonationField.donationPersonalNote}
          minH="200px"
          {...getDataTracking('p2p_df_setpersonalnote')}
          {...register(DonationField.donationPersonalNote)}
        />
      </FormControl>
    </VStack>
  );
};
